import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Delete, DownloadIcon, Edit, ReplaceIcon, ViewIcon } from '../../../assets/svg';
import {
    allManagementRole,
    isAdminRole,
    languageOptions,
    QUERY_KEYS,
    ROLES,
    TABLE_ID,
    uptoContentManagerRole,
} from '../../../constants';
import { getContentTypeByExtension, getFileExtension, popup } from '../../../utils';
import {
    deleteSafetyMeetingService,
    updateSafetyMeetingsService,
} from '../../../utils/apiServices/safetyServiceAPI';
import { AddSafetyMeetingModal } from '../../shared/Modal/AddSafetyMeetingModal';
import { DeleteConfirmationModal } from '../../shared';
import { ReplaceSafetyMeetingModal } from '../../shared/Modal/ReplaceSafetyMeetingModal';
import useViewProcessedFile from '../../../hooks/useViewProcessedFile';
import useBulkDownloadFiles from '../../../hooks/useBulkDownloadFiles';
import useFileHandler from '../../../hooks/useFileHandler';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const SafetyMeetingActions = ({ data, tableId }) => {
    const queryClient = useQueryClient();
    const { id, file_name, language, industries, s3_key } = data;
    const extension = getFileExtension(s3_key);
    const fileType = getContentTypeByExtension(extension);
    const { viewProcessedFile } = useViewProcessedFile();
    const { batchDownloadFiles } = useBulkDownloadFiles();
    const { viewHandler, downloadHandler } = useFileHandler({
        fileType,
        file_name,
        s3_key,
        extension,
    });

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const accountName = useSelector((s) => s.accountArray.selectedAccountName);

    const currentRole = useSelector((s) => s.user.role);
    const isAdmin = isAdminRole(currentRole);
    const isUser = currentRole === ROLES.USER;
    const hasViewPrivileges = allManagementRole(currentRole);
    const hasDownloadAndEditPrivileges = uptoContentManagerRole(currentRole);

    const editMutation = useMutation({
        mutationFn: async (values) =>
            await updateSafetyMeetingsService({
                data: {
                    file_name: values.fileName,
                    industries: values.industries?.map((industry) => industry.value),
                    language: values.language?.value,
                },
                id,
            }),
        onSuccess: ({ message }) => {
            popup('success', message);
            setShowEditModal(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING],
                exact: false,
            });
        },
        onError: (error) => popup('error', error.message),
    });

    const deleteMutation = useMutation({
        mutationFn: async () =>
            await deleteSafetyMeetingService({
                data: { id, s3_key },
            }),
        onSuccess: ({ message }) => {
            popup('success', message);
            setIsDeleteModalOpen(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING],
                exact: false,
            });
        },
        onError: (error) => popup('error', error.message),
    });

    const deleteHandler = async () => {
        deleteMutation.mutate();
    };

    const viewProcessedFileHandler = () => {
        const data = {
            s3_key,
            logoUrl: '',
            customerName: accountName,
            fileName: file_name,
        };
        viewProcessedFile(data);
    };

    const processedDownloadHandler = () => {
        const data = {
            s3_key,
            logoUrl: '',
            customerName: accountName,
            fileName: file_name,
        };
        batchDownloadFiles([data]);
    };

    const editModalOnSubmit = async (values) => {
        editMutation.mutate(values);
    };

    return (
        <div className='d-flex align-items-center' style={{ gap: 8 }}>
            {tableId === TABLE_ID.SAFETY_MEETINGS && hasDownloadAndEditPrivileges && (
                <span title={'Edit'} id='action-btn' onClick={() => setShowEditModal(true)}>
                    <Edit />
                </span>
            )}
            {[TABLE_ID.SAFETY_MEETINGS, TABLE_ID.ASSIGNED_SAFETY_MEETINGS].includes(tableId) &&
                hasViewPrivileges && (
                    <span title={'View'} id='action-btn' onClick={viewHandler}>
                        <ViewIcon />
                    </span>
                )}
            {tableId === TABLE_ID.ASSIGNED_SAFETY_MEETINGS && isUser && (
                <span title={'View'} id='action-btn' onClick={viewProcessedFileHandler}>
                    <ViewIcon />
                </span>
            )}
            {tableId === TABLE_ID.ASSIGNED_SAFETY_MEETINGS && isUser && (
                <span title={'Download'} id='action-btn' onClick={processedDownloadHandler}>
                    <DownloadIcon />
                </span>
            )}
            {tableId === TABLE_ID.ASSIGNED_SAFETY_MEETINGS && hasDownloadAndEditPrivileges && (
                <span title={'Download'} id='action-btn' onClick={downloadHandler}>
                    <DownloadIcon />
                </span>
            )}
            {tableId === TABLE_ID.SAFETY_MEETINGS && (
                <span title={'Replace'} id='action-btn' onClick={() => setShowReplaceModal(true)}>
                    <ReplaceIcon size={16} />
                </span>
            )}
            {tableId === TABLE_ID.SAFETY_MEETINGS && isAdmin && (
                <span title={'Delete'} id='action-btn' onClick={() => setIsDeleteModalOpen(true)}>
                    <Delete />
                </span>
            )}
            {showEditModal && hasDownloadAndEditPrivileges && (
                <AddSafetyMeetingModal
                    isEdit={true}
                    showAddModal={showEditModal}
                    setShowAddModal={setShowEditModal}
                    editHandler={editModalOnSubmit}
                    editDetails={{
                        file_name,
                        industries: JSON.parse(industries)?.map((i) => ({ label: i, value: i })),
                        language: languageOptions.find((l) => l.value === language),
                        type: { label: s3_key.split('/')[1], value: s3_key.split('/')[1] },
                    }}
                    isLoading={editMutation.isPending}
                />
            )}
            <DeleteConfirmationModal
                isDeleteModal={isDeleteModalOpen}
                setIsDeleteModal={setIsDeleteModalOpen}
                selectedItemId={id}
                handleDeleteClick={deleteHandler}
                deleteButtonName={`${file_name} Templates`}
                isLoading={deleteMutation.isPending}
            />
            {showReplaceModal && (
                <ReplaceSafetyMeetingModal
                    showModal={showReplaceModal}
                    setShowModal={setShowReplaceModal}
                    id={id}
                    fileName={file_name}
                />
            )}
        </div>
    );
};
